import React from "react";
import { Box, Typography, Button } from "minimal-ui";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { parseAvailableQuantityStockMessage } from "../Epos/utils/telecallerStockLimit";

const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const mergeRanges = (ranges) => {
  if (!ranges.length) return [];
  const sorted = [...ranges].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const out = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const [a0, a1] = out[out.length - 1];
    const [b0, b1] = sorted[i];
    if (b0 <= a1) {
      out[out.length - 1] = [a0, Math.max(a1, b1)];
    } else {
      out.push([b0, b1]);
    }
  }
  return out;
};

const collectEmphasisRanges = (text, { productName = "", productCode = "" } = {}) => {
  const t = String(text);
  const ranges = [];

  const reNum = /\d+(?:\.\d+)?/g;
  let m;
  while ((m = reNum.exec(t)) !== null) {
    ranges.push([m.index, m.index + m[0].length]);
  }

  const reQuote = /"([^"]*)"|'([^']*)'/g;
  while ((m = reQuote.exec(t)) !== null) {
    ranges.push([m.index, m.index + m[0].length]);
  }

  const name = String(productName || "").trim();
  if (name.length > 0) {
    const reN = new RegExp(`\\b${escapeRegExp(name)}\\b`, "gi");
    while ((m = reN.exec(t)) !== null) {
      ranges.push([m.index, m.index + m[0].length]);
    }
  }

  const code = String(productCode || "").trim();
  if (code.length > 0 && code !== name) {
    const reC = new RegExp(`\\b${escapeRegExp(code)}\\b`, "g");
    while ((m = reC.exec(t)) !== null) {
      ranges.push([m.index, m.index + m[0].length]);
    }
  }

  return mergeRanges(ranges);
};

const renderEmphasizedMessage = (text, opts) => {
  const t = String(text);
  const ranges = collectEmphasisRanges(t, opts);
  if (!ranges.length) {
    return t;
  }
  const parts = [];
  let i = 0;
  let k = 0;
  for (const [a, b] of ranges) {
    if (a > i) {
      parts.push(
        <React.Fragment key={`t-${k++}`}>{t.slice(i, a)}</React.Fragment>
      );
    }
    parts.push(
      <Box
        component="span"
        key={`b-${a}-${b}`}
        sx={{ fontWeight: 700 }}
      >
        {t.slice(a, b)}
      </Box>
    );
    i = b;
  }
  if (i < t.length) {
    parts.push(
      <React.Fragment key={`t-${k++}`}>{t.slice(i)}</React.Fragment>
    );
  }
  return <>{parts}</>;
};

const plainErrorTitle = (msg) => {
  if (!msg || typeof msg !== "string") return "Error";
  if (/out of stock/i.test(msg)) return "Out of stock";
  if (
    /available quantity|stock limit|maximum quantity reached|qty reached|quantity reached|only\s+\d+|left in stock/i.test(
      msg
    )
  ) {
    return "Stock limit reached";
  }
  return "Error";
};

export default function SalesStockLimitDialog({
  open,
  onClose,
  productName = "",
  productCode = "",
  maxQty,
  message = "",
  plainTitle,
}) {
  const handleDialogKeyDown = (event) => {
    if (event.key !== "Enter" || event.repeat) return;
    event.preventDefault();
    onClose(event);
  };

  if (!open) return null;

  const parsed =
    message && typeof message === "string"
      ? parseAvailableQuantityStockMessage(message)
      : null;

  let displayName = productName;
  let displayQty = maxQty;
  if (parsed) {
    displayName = parsed.productName;
    displayQty = parsed.maxQty;
  }

  const displayCodeRaw = String(
    (productCode && String(productCode).trim()) || ""
  );
  const nameTrim = String(displayName || "").trim();
  const displayCode =
    displayCodeRaw && displayCodeRaw !== nameTrim
      ? displayCodeRaw
      : "";
  const qtyNum = Number(displayQty);
  const hasStructured =
    Boolean(displayName) &&
    displayQty !== undefined &&
    displayQty !== null &&
    Number.isFinite(qtyNum);

  if (!hasStructured && message) {
    const parsedForTitle = parseAvailableQuantityStockMessage(message);
    const dialogTitle =
      plainTitle ||
      (parsedForTitle
        ? Number(parsedForTitle.availableQty) > 0 &&
          Number(parsedForTitle.maxQty) > 0
          ? "Stock limit reached"
          : "Out of stock"
        : plainErrorTitle(message));
    return (
      <Dialog
        open={open}
        onClose={onClose}
        onKeyDown={handleDialogKeyDown}
        maxWidth="sm"
        fullWidth={false}
        disableScrollLock
        slotProps={{
          root: { sx: { zIndex: 2000 } },
          backdrop: {
            sx: {
              zIndex: 1999,
              backgroundColor: "rgba(0, 0, 0, 0.06)",
            },
          },
        }}
        PaperProps={{
          sx: { width: "400px" },
        }}
      >
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" component="div">
            {renderEmphasizedMessage(message, { productName, productCode })}
          </Typography>
        </DialogContent>
        <DialogActions >
          <Button className="button-variant-contained" sx={{ cursor: 'pointer' }} onClick={onClose}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!hasStructured) {
    const fallbackQty = Number.isFinite(qtyNum) ? qtyNum : 0;
    const fallbackTitle =
      plainTitle || (fallbackQty > 0 ? "Stock limit reached" : "Out of stock");
    const fallbackMessage =
      fallbackQty > 0
        ? `Available quantity is ${fallbackQty} only.`
        : "This product is out of stock.";
    return (
      <Dialog
        open={open}
        onClose={onClose}
        onKeyDown={handleDialogKeyDown}
        maxWidth="sm"
        fullWidth={false}
        disableScrollLock
        slotProps={{
          root: { sx: { zIndex: 2000 } },
          backdrop: {
            sx: {
              zIndex: 1999,
              backgroundColor: "rgba(0, 0, 0, 0.06)",
            },
          },
        }}
        PaperProps={{
          sx: { width: "400px" },
        }}
      >
        <DialogTitle>{fallbackTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" component="div">
            {renderEmphasizedMessage(fallbackMessage, { productName, productCode })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button className="button-variant-contained" sx={{ cursor: "pointer" }} onClick={onClose}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const title = qtyNum > 0 ? "Stock limit reached" : "Out of stock";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onKeyDown={handleDialogKeyDown}
      maxWidth="sm"
      fullWidth={false}
      disableScrollLock
      slotProps={{
        root: { sx: { zIndex: 2000 } },
        backdrop: {
          sx: {
            zIndex: 1999,
            backgroundColor: "rgba(0, 0, 0, 0.06)",
          },
        },
      }}
      PaperProps={{
        sx: { width: "400px" },
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {qtyNum > 0 ? (
          <Typography variant="body2" component="div">
            Available quantity of{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {displayName}
            </Box>
            {displayCode ? (
              <>
                {" "}
                (
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {displayCode}
                </Box>
                )
              </>
            ) : null}{" "}
            is{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {qtyNum}
            </Box>{" "}
            only. So can add maximum{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {qtyNum}
            </Box>
            .
          </Typography>
        ) : (
          <Typography variant="body2" component="div">
            Available quantity of{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              {displayName}
            </Box>
            {displayCode ? (
              <>
                {" "}
                (
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {displayCode}
                </Box>
                )
              </>
            ) : null}{" "}
            is{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              0
            </Box>{" "}
            only.{" "}
            <Box component="span" sx={{ fontWeight: 700 }}>
              Out of stock
            </Box>
            .
          </Typography>
        )}
      </DialogContent>
      <DialogActions >
        <Button className="button-variant-contained" sx={{ cursor: 'pointer' }} onClick={onClose}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
